import { useQuery, useQueries } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";
import { buildQueryParams } from "../lib/globalUtils";
import {
  buildUserWhereClause,
  paramsToSend,
  getEnabledUserTypes,
  isUserTypeEnabled,
} from "../constants/userConstants";

/**
 * Hook to get users with optimized caching
 * Prevents multiple refetch and reduces API calls
 */
export const useUsers = (params = {}) => {
  const { enabled = true, ...queryParams } = params;
  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["users", queryParams],
    queryFn: async () => {
      const response = await apiClient.get(
        `/users${queryString ? `?${queryString}` : ""}`,
      );
      return {
        users: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled: isEndpointEnabled("dashboard", "users") && enabled,
    staleTime: 10 * 60 * 1000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("dashboard", "users"),
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Optimized hook to get all user counts and stats in a single batch
 * Only queries enabled user types
 */
export const useUsersStats = (enabledFilter = "all", searchTerm = "") => {
  const enabledUserTypes = getEnabledUserTypes();

  const queries = enabledUserTypes.map((userType) => ({
    queryKey: ["users", "stats", userType, enabledFilter, searchTerm],
    queryFn: async () => {
      const whereClause = buildUserWhereClause(
        userType,
        enabledFilter,
        searchTerm,
      );
      const params = {
        ...paramsToSend,
        where: whereClause,
        page: 1,
        page_size: paramsToSend.page_size,
      };
      const queryString = buildQueryParams(params);
      const response = await apiClient.get(
        `/users${queryString ? `?${queryString}` : ""}`,
      );
      return {
        total: response?.pagination?.total || 0,
        pagination: response?.pagination || {},
        users: response?.result || [],
      };
    },
    enabled: isEndpointEnabled("dashboard", "users"),
    staleTime: 10 * 60 * 1000,
  }));

  const results = useQueries({ queries });

  const stats = {};
  const tabCounts = {};
  let totalUsers = 0;

  enabledUserTypes.forEach((userType, index) => {
    const count = results[index]?.data?.total || 0;
    stats[
      `total${userType.charAt(0).toUpperCase() + userType.slice(1).replace("-", "")}`
    ] = count;
    tabCounts[userType] = count;
    totalUsers += count;
  });

  stats.totalUsers = totalUsers;

  return {
    isLoading: results.some((result) => result.isLoading),
    error: results.find((result) => result.error)?.error,
    data: stats,
    tabCounts,
    results,
  };
};

const FILTER_KEYS = ["all", "enabled", "disabled"];

/**
 * Hook that fetches users with exactly 3 queries:
 * - Active tab: full data with current page
 * - Other 2 tabs: page_size=1 for count only
 * Returns users, pagination, tab counts, and stats.
 */
export const useUsersData = (
  userType,
  enabledFilter = "all",
  searchTerm = "",
  currentPage = 1,
) => {
  const isEnabled =
    isEndpointEnabled("dashboard", "users") && isUserTypeEnabled(userType);

  const results = useQueries({
    queries: FILTER_KEYS.map((filter) => {
      const isActiveTab = filter === enabledFilter;
      const whereClause = buildUserWhereClause(userType, filter, searchTerm);
      const params = {
        ...paramsToSend,
        where: whereClause,
        page: isActiveTab ? currentPage : 1,
      };
      const queryString = buildQueryParams(params);

      return {
        queryKey: [
          "users",
          "tab-data",
          userType,
          filter,
          searchTerm,
          isActiveTab ? currentPage : 1,
        ],
        queryFn: async () => {
          const response = await apiClient.get(
            `/users${queryString ? `?${queryString}` : ""}`,
          );
          return {
            users: response?.result || [],
            pagination: response?.pagination || {},
          };
        },
        enabled: isEnabled,
        staleTime: 10 * 60 * 1000,
        refetchInterval: false,
        refetchOnWindowFocus: false,
        refetchOnMount: isEnabled,
        refetchOnReconnect: false,
        retry: false,
      };
    }),
  });

  // Build tab counts from pagination totals
  const filterTabCounts = {};
  FILTER_KEYS.forEach((filter, i) => {
    filterTabCounts[filter] = results[i]?.data?.pagination?.total || 0;
  });

  // Active tab data
  const activeIndex = FILTER_KEYS.indexOf(enabledFilter);
  const activeResult = results[activeIndex];

  // Stats
  const stats = {
    [`total${userType.charAt(0).toUpperCase() + userType.slice(1).replace("-", "")}`]:
      filterTabCounts.all,
    totalUsers: filterTabCounts.all,
  };

  return {
    stats,
    filterTabCounts,
    users: activeResult?.data?.users || [],
    pagination: activeResult?.data?.pagination || {},
    isLoading: activeResult?.isLoading || false,
    error: activeResult?.error || null,
  };
};

/**
 * Hook to get users for current tab with pagination
 */
export const useUsersForTab = (
  userType,
  enabledFilter = "all",
  searchTerm = "",
  currentPage = 1,
) => {
  const whereClause = buildUserWhereClause(userType, enabledFilter, searchTerm);

  return useQuery({
    queryKey: [
      "users",
      "tab",
      userType,
      enabledFilter,
      searchTerm,
      currentPage,
    ],
    queryFn: async () => {
      const params = {
        ...paramsToSend,
        where: whereClause,
        page: currentPage,
      };
      const queryString = buildQueryParams(params);
      const response = await apiClient.get(
        `/users${queryString ? `?${queryString}` : ""}`,
      );
      return {
        users: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled:
      isEndpointEnabled("dashboard", "users") && isUserTypeEnabled(userType),
    staleTime: 10 * 60 * 1000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("dashboard", "users"),
    refetchOnReconnect: false,
    retry: false,
  });
};

export default {
  useUsers,
  useUsersStats,
  useUsersForTab,
  useUsersData,
};
